import { Document } from '@contentful/rich-text-types';
import { AnchorButton } from '@soluto-home-web/platform-common-ui';
import { Asset } from 'contentful';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import { ENTERED, ENTERING, EXITED, EXITING, TransitionStatus } from 'react-transition-group/Transition';
import styled from 'styled-components';
import RichText from '../../../../components/RichText';
import { LinkId } from '../../../../constants';
import { RefundsSeparator } from '../../components/CommonComponents';
import NeedHelpButton from '../../components/NeedHelpButton';
import ExpandIcon from './ExpandIcon';
import { AnchorWithAnalytics } from '@soluto-home-web/platform-common-ui';

const s = {
  Section: styled.section`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: start;
  `,
  Title: styled.h1`
    font-size: 24px;
    align-self: center;
    margin-top: 40px;
  `,
  Description: styled.h2`
    font-size: 18px;
    margin: 28px 0;
  `,
  ExpandButton: styled(AnchorWithAnalytics)`
    color: inherit;
    text-align: start;
    padding: 0.5em 0;
    display: flex;
    align-items: center;
    margin-bottom: calc(40px - 1em);
    font-size: 0.8em;
  `,
  ExpandIcon: styled(ExpandIcon)`
    margin-right: 0.5em;
  `,
  Content: styled.div<{ status: TransitionStatus }>`
    font-size: 16px;
    line-height: 155.74%;
    overflow: hidden;
    width: 100%;
    opacity: ${({ status }) => (status === ENTERING || status === ENTERED ? 1 : 0)};
    transition: ${({ status }) =>
      status === ENTERING || status === ENTERED
        ? 'height 0.4s ease-in-out, opacity 0.35s ease-in-out 0.35s'
        : 'opacity 0.35s ease-in-out, height 0.4s ease-in-out 0.3s'};
    visibility: ${({ status }) => (status === EXITED ? 'hidden' : 'visible')};

    a {
      color: inherit;
    }
  `,
  NeedHelpButton: styled(NeedHelpButton)`
    margin: 40px auto;
  `,
  Separator: styled(RefundsSeparator)`
    margin: 0.5em 0;
  `,
};

type ContentProps = {
  content: Document;
  status: TransitionStatus;
  title: string;
};

const Content = ({ content, status, title }: ContentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (status === ENTERING) {
      setHeight(ref.current!.scrollHeight);
    } else if (status === EXITING) {
      setHeight(0);
    }
  }, [status]);

  return (
    <s.Content ref={ref} status={status} style={{ height }}>
      <RichText content={content} />
      <s.NeedHelpButton
        id={LinkId.RefundGuideNeedHelpCall}
        text="Need help? Message us."
        analyticsExtras={{ ElementName: title }}
      />
    </s.Content>
  );
};

export type RefundSectionProps = {
  title: string;
  description: string;
  content: Document;
};

export const RefundSection = ({ title, description, content }: RefundSectionProps) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <s.Section>
        <s.Title>{title}</s.Title>
        <s.Description>{description}</s.Description>
        <s.ExpandButton
          onClick={() => setExpanded((e) => !e)}
          analyticsExtras={{ ElementName: title, ElementId: 'ShowMeHow' }}
        >
          <s.ExpandIcon collapse={expanded} />
          <u>Show me how</u>
        </s.ExpandButton>
        <Transition in={expanded} timeout={700}>
          {(status) => <Content status={status} content={content} title={title} />}
        </Transition>
      </s.Section>
      <s.Separator />
    </>
  );
};

export default RefundSection;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/profile/subpages/RefundGuidePage/RefundSection.tsx